import type { MailboxInfo, MailboxKey } from 'owa-client-types';
import { getMailboxKeyForMailboxInfo } from 'owa-client-types';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import type TableView from '../../store/schema/TableView';
import { isMultiAccountTable } from '../isMultiAccountTable';
import { getMailboxInfoFromRowKeyOrId } from './getMailboxInfoFromRowKeyOrId';
import { groupByMailboxKey } from './groupByMailboxKey';

export interface MailboxGroup {
    mailboxInfo: MailboxInfo;
    rowKeys: string[];
}

/**
 * Groups selected row keys by their actual mailbox, keyed by MailboxKey for stable identity.
 * For single-mailbox tables, returns a single group with the default mailboxInfo.
 *
 * Throws if any row key cannot be resolved to a mailbox — callers should not
 * silently drop unresolvable rows.
 */
export function groupRowKeysByMailboxInfo(
    defaultMailboxInfo: MailboxInfo,
    rowKeysOrIds: string[],
    tableView?: TableView
): Map<MailboxKey, MailboxGroup> {
    if (!isUnifiedInboxMailboxInfo(defaultMailboxInfo) && !isMultiAccountTable(tableView)) {
        const groups = new Map<MailboxKey, MailboxGroup>();
        const key = getMailboxKeyForMailboxInfo(defaultMailboxInfo);
        groups.set(key, {
            mailboxInfo: defaultMailboxInfo,
            rowKeys: [...rowKeysOrIds],
        });
        return groups;
    }

    const grouped = groupByMailboxKey(rowKeysOrIds, rowKeyOrId => {
        const mailboxInfo = getMailboxInfoFromRowKeyOrId(rowKeyOrId, tableView);
        if (!mailboxInfo) {
            throw new Error(
                'For UnifiedInbox or Multi-Account Search, unable to resolve mailbox info for a row key or item ID.'
            );
        }
        return mailboxInfo;
    });

    if (grouped.size === 0) {
        throw new Error('groupRowKeysByMailboxInfo: no groups produced — rowKeysOrIds was empty.');
    }

    const groups = new Map<MailboxKey, MailboxGroup>();
    for (const [key, group] of grouped) {
        groups.set(key, {
            mailboxInfo: group.mailboxInfo,
            rowKeys: group.items,
        });
    }

    return groups;
}
